import React, { useContext, useEffect, useMemo, useState } from 'react';
import TitleSearch from '@/components/_common/TitleVersionSelect/TitleSearch';
import { TitleEntity } from '@/redux/slices/slice_types';
import { Box, darken, Fade, FormControlLabel, Grid, IconButton, lighten, MenuItem, Radio, RadioGroup, Typography } from '@mui/material';
import TitleTypeLabel from '@/components/_common/TitleTypeLabel';
import DropdownSelect from '@/components/_common/Form/DropdownSelect';
import axios from '@/_utils/axios';
import styled from '@emotion/styled';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { getLineageMetaTitle, capitalizeNoun } from '@/_utils/utils';
import { Spinner } from '@/components/_common';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { selectIngestManagementRecentTitles } from '@/redux/selectors/ingestManagementSelectors';

export type SelectedTitles = { options: TitleEntity[]; value: TitleEntity | null }[];

interface TitleAssociationProps {
  onRegisterEntitySelect: (entity: TitleEntity, titles: SelectedTitles) => void;
  handleClearForm: () => void;
}

// when a parent title (selectedParent) is selected, display the "register against" options (child_entity_types)
// when a "register against" option is selected, display the hierarchy selection dropdowns
// if "series" or "feature" (or any top-level entity) is selected, no dropdowns should be displayed, and we are done and can call onRegisterEntitySelect
// otherwise, display the dropdowns until we reach the `registerAgainst` entity type

const TitleAssociation = ({ onRegisterEntitySelect, handleClearForm }: TitleAssociationProps) => {
  const { registerAgainstWhiteList, versionRender } = useContext(ConfigContext).ingestManagementConfig;
  const disableRecentTitles = useContext(ConfigContext).ingestManagementConfig.disableRecentTitles;
  const recentTitles = !disableRecentTitles ? useAppSelector(selectIngestManagementRecentTitles) : [];
  const [selectedParent, setSelectedParent] = useState<TitleEntity | null>(null);
  const [showSearch, setShowSearch] = useState(true);
  const [selectedTitles, setSelectedTitles] = useState<SelectedTitles>([]);
  const [loadingStates, setLoadingStates] = useState<boolean[]>([]);
  const [registerAgainst, setRegisterAgainst] = useState<null | string>(null);
  const [childEntityTypes, setChildEntityTypes] = useState<Set<string>>(new Set<string>());
  const [selectedRecentTitles, setSelectedRecentTitles] = useState<SelectedTitles | null>(null);
  const [loadingChildEntityTypes, setLoadingChildEntityTypes] = useState(false);

  const registerAgainstOptions = useMemo(() => {
    if (!selectedParent || !childEntityTypes) return [];
    const options = [selectedParent.entity_type].concat(Array.from(childEntityTypes)).filter((type) => type !== undefined);
    if (registerAgainstWhiteList && registerAgainstWhiteList.length > 0) {
      return options.filter((type) => registerAgainstWhiteList.includes(type)).map((type) => ({ key: type, label: capitalizeNoun(type) }));
    }
    return options.map((type) => ({ key: type, label: capitalizeNoun(type) }));
  }, [childEntityTypes]);

  const fetchChildEntityTypes = async () => {
    if (selectedParent) {
      setLoadingChildEntityTypes(true);
      try {
        const res = await axios.get(`/inventory/titles/${selectedParent.id}/`);
        if (!res?.data?.data) return [];

        // if it's an array of strings, return the array
        // if it's an array of objects, return the array of entity_types if hidden is false
        const children = res.data?.data?.child_entity_types || [];
        const childEntityTypes = new Set<string>();
        children.forEach((child: TitleEntity) => {
          if (typeof child === 'string') {
            childEntityTypes.add(child);
          } else if (child.hidden === false || child.hidden === 'false') {
            // lol
            childEntityTypes.add(child.entity_type);
          }
        });
        setChildEntityTypes(childEntityTypes);
      } catch (error) {
        console.error('Error fetching child entity types:', error);
      } finally {
        setLoadingChildEntityTypes(false);
      }
    }
  };

  useEffect(() => {
    fetchChildEntityTypes();
  }, [selectedParent]);

  const fetchTitle = async (id: number) => {
    const res = await axios.get(`/inventory/titles/${id}/`);
    let children = res.data?.data?.children || [];
    children = children.sort((a: TitleEntity, b: TitleEntity) =>
      a.sequence_order === 0 ? 1 : b.sequence_order === 0 ? -1 : Number(a.sequence_order) - Number(b.sequence_order)
    );
    return children;
  };

  const getChildren = async (parent: TitleEntity, index: number) => {
    if (!parent) return;

    setLoadingStates((prev) => {
      const newLoadingStates = [...prev];
      newLoadingStates[index] = true;
      return newLoadingStates;
    });

    try {
      const children = await fetchTitle(parent.id);
      setSelectedTitles((prev) => {
        const newSelectedTitles = [...prev];
        if (newSelectedTitles.length > index) {
          newSelectedTitles[index] = { options: children, value: null };
        } else {
          newSelectedTitles.push({ options: children, value: null });
        }
        return newSelectedTitles;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStates((prev) => {
        const newLoadingStates = [...prev];
        newLoadingStates[index] = false;
        return newLoadingStates;
      });
    }
  };

  const handleTitleSelect = (title: TitleEntity, index: number) => {
    if (title.entity_type === registerAgainst) {
      const st = [
        { options: [selectedParent as TitleEntity], value: selectedParent as TitleEntity },
        ...selectedTitles.slice(0, index).concat({ options: selectedTitles[index]?.options || [], value: title })
      ];
      onRegisterEntitySelect(title, st);
    } else {
      getChildren(title, index + 1);
    }
    setSelectedTitles((prev) => {
      if (!disableRecentTitles && selectedRecentTitles) {
        setSelectedRecentTitles(null);
        return selectedRecentTitles;
      }
      const newSelectedTitles = prev.slice(0, index).concat({ options: prev[index]?.options || [], value: title });
      return newSelectedTitles;
    });
  };

  const handleTitleSelectParent = (title: TitleEntity) => {
    setShowSearch(false);
    setSelectedParent(title);
    if (title.id === selectedParent?.id) fetchChildEntityTypes();
    setRegisterAgainst(null);
    setSelectedTitles([]);
  };

  const handleRegisterAgainstChange = async (event: React.ChangeEvent<HTMLInputElement> | string) => {
    const selectedValue = typeof event === 'string' ? event : event.target.value;
    setRegisterAgainst(selectedValue);
    if (selectedParent && selectedParent?.entity_type === selectedValue) {
      onRegisterEntitySelect(selectedParent, [{ options: [selectedParent as TitleEntity], value: selectedParent as TitleEntity }]);
    } else if (selectedParent) {
      if (disableRecentTitles || !selectedRecentTitles) await getChildren(selectedParent, 0);
    }
  };

  useEffect(() => {
    handleClearForm();
    if (disableRecentTitles || !selectedRecentTitles?.length || !registerAgainst) {
      setSelectedTitles([]);
    } else {
      const index = selectedRecentTitles.length - 1;
      handleTitleSelect(selectedRecentTitles[index].value as TitleEntity, index);
    }
  }, [registerAgainst]);

  const getAppend = (child: TitleEntity) => {
    if (child.entity_type === 'version') {
      return (
        <Typography variant="caption" sx={{ ml: 0.75 }}>
          [ID: {child.external_id}]
        </Typography>
      );
    }
    if (child.entity_type === 'ova') {
      return (
        <Typography variant="caption" sx={{ ml: 0.75 }}>
          [OVA]
        </Typography>
      );
    }
  };

  const getPrepend = (child: TitleEntity) => {
    if (child.entity_type === 'episode') {
      return (
        <Typography variant="caption" sx={{ mr: 0.75 }}>
          E{child.sequence_order}:
        </Typography>
      );
    }
  };

  const onStartOver = () => {
    setSelectedParent(null);
    setSelectedTitles([]);
    setShowSearch(true);
    handleClearForm();
    setRegisterAgainst(null);
    setChildEntityTypes(new Set<string>());
    setLoadingChildEntityTypes(false);
  };

  const handleRecentTitleClick = (titles: SelectedTitles) => {
    if (disableRecentTitles) return;
    if (selectedTitles.length && titles.at(-1)?.value?.id === selectedTitles.at(-1)?.value?.id) return;
    const title = titles[0].value;
    if (title) handleTitleSelectParent(title);
    setSelectedRecentTitles(titles.slice(1, titles.length));
  };

  useEffect(() => {
    if (disableRecentTitles) {
      if (registerAgainstOptions?.length === 1) {
        handleRegisterAgainstChange(registerAgainstOptions[0].key);
      }
      return;
    }

    const test = (selectedRecentTitles || []).at(-1)?.value?.entity_type ?? '';
    if (selectedRecentTitles?.length && registerAgainstOptions?.length && test.length > 0) {
      handleRegisterAgainstChange(test);
    } else if (registerAgainstOptions?.length === 1) {
      handleRegisterAgainstChange(registerAgainstOptions[0].key);
    }
  }, [registerAgainstOptions]);

  return (
    <div>
      {!disableRecentTitles && recentTitles.length > 0 && (
        <RecentTitlesWrapper>
          <RecentTitlesHeader>Recently Selected Titles</RecentTitlesHeader>
          <RecentTitlesContainer>
            {recentTitles.map((titles) => {
              if (!titles || !Array.isArray(titles)) return null;
              const curVersion = titles.at(-1)?.value?.entity_type === 'version' ? titles.at(-1)?.value : null;
              const curTitle = curVersion ? (titles.at(-2)?.value ?? undefined) : (titles.at(-1)?.value ?? undefined);
              return (
                <RecentTitle
                  active={titles.at(-1)?.value?.id === selectedTitles.at(-1)?.value?.id}
                  onClick={() => handleRecentTitleClick(titles)}
                  key={titles.at(-1)?.value?.id}
                >
                  <Typography>
                    <strong>{curTitle?.name}</strong>
                    {curVersion && (
                      <Typography variant="caption">
                        {` | ${typeof versionRender !== 'undefined' ? versionRender(curVersion) : curVersion.name}`}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1.9, fontSize: '.9rem' }}>
                    {getLineageMetaTitle(curTitle, true)}
                  </Typography>
                </RecentTitle>
              );
            })}
          </RecentTitlesContainer>
        </RecentTitlesWrapper>
      )}
      <Fade in={showSearch} timeout={300} unmountOnExit>
        <div>
          <TitleSearch onTitleSelect={handleTitleSelectParent} />
        </div>
      </Fade>
      <Fade in={!showSearch && !!selectedParent} timeout={300} unmountOnExit>
        <div>
          {selectedParent && (
            <SelectedParentDisplay>
              <TitleDisplayBox>
                <Grid container alignItems="center">
                  <Typography component="span" variant="h4" sx={{ mr: 2 }}>
                    {selectedParent.name}
                  </Typography>
                  <TitleTypeLabel type={selectedParent.entity_type} small />
                  <StyledIconButton onClick={onStartOver} sx={{ ml: 'auto' }} color="default">
                    <PolarisIcon icon="undo" height={30} />
                  </StyledIconButton>
                </Grid>
              </TitleDisplayBox>
              {registerAgainstOptions.length > 1 && (
                <RegisterAgainstBox>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Register Against
                  </Typography>
                  <RadioGroup row value={registerAgainst} onChange={handleRegisterAgainstChange}>
                    {selectedParent && registerAgainstOptions.length > 0 ? (
                      registerAgainstOptions.map((option) => {
                        const isSelected = registerAgainst === option.key;
                        return (
                          <StyledOption
                            key={option.key}
                            value={option.key}
                            control={<StyledRadio selected={+isSelected} />}
                            label={option.label}
                            selected={+isSelected}
                            labelPlacement="start"
                          />
                        );
                      })
                    ) : (
                      <div style={{ height: 50, width: '100%' }}>
                        <Spinner contained size={30} />
                      </div>
                    )}
                  </RadioGroup>
                </RegisterAgainstBox>
              )}
              <Grid container alignItems="center" spacing={2} sx={{ pt: 0.5 }}>
                {loadingChildEntityTypes ? (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Spinner contained size={30} />
                  </Grid>
                ) : registerAgainst && selectedTitles.length > 0 ? (
                  selectedTitles.map((title, index) => {
                    const options = title.options;
                    const entityType = !title.value ? options[0]?.entity_type : title.value.entity_type;
                    return (
                      <Fade in timeout={(index + 1) * 300} key={index}>
                        <Grid item xs={12} lg={6} xl={3} sx={{ mb: 3 }} key={index}>
                          <DropdownSelect
                            loading={loadingStates[index]}
                            label={!loadingStates[index] && !options.length ? `No Options Available` : `Select ${entityType}`}
                            disabled={!options.length}
                            value={title.value || ''}
                            onChange={(val) => handleTitleSelect(val, index)}
                            fullWidth
                          >
                            {options.map((child) => (
                              <MenuItem key={child.id} value={child as any}>
                                {getPrepend(child)}
                                {child.name}
                                {getAppend(child)}
                              </MenuItem>
                            ))}
                          </DropdownSelect>
                        </Grid>
                      </Fade>
                    );
                  })
                ) : registerAgainst && selectedTitles.length === 0 ? (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Spinner contained size={30} />
                  </Grid>
                ) : (
                  <></>
                )}
              </Grid>
            </SelectedParentDisplay>
          )}
        </div>
      </Fade>
    </div>
  );
};

export default TitleAssociation;

const RecentTitlesWrapper = styled(Box)`
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  padding: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const RecentTitlesHeader = styled(Typography)`
  font-size: 1.2rem;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;
const RecentTitlesContainer = styled(Box)`
  width: 100%;
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow: auto;
`;

const RecentTitle = styled(Box)<{ active: boolean }>`
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  min-width: 300px;
  cursor: ${({ active }) => (active ? 'default' : 'pointer')};
  background: ${({ active, theme }) => (active ? darken(theme.palette.background.paper, 0.2) : 'inherit')};
  &:hover {
    background: ${({ theme }) => darken(theme.palette.background.paper, 0.2)};
  }
`;

const SelectedParentDisplay = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing(1)};
`;

const TitleDisplayBox = styled(Box)`
  background: ${({ theme }) => theme.palette.background.default};
  padding: 10px 10px 10px 20px;
  border-radius: 13px;
  display: flex;
  align-items: center;

  .MuiInputBase-root {
    background: ${({ theme }) => lighten(theme.palette.background.paper, 0.05)};
  }

  .MuiFormLabel-root.MuiFormLabel-root {
    text-transform: capitalize;
  }
`;

const RegisterAgainstBox = styled(Box)`
  background: ${({ theme }) => theme.palette.background.default};
  padding: 10px 20px;
  border-radius: 13px;
`;

const StyledIconButton = styled(IconButton)`
  opacity: 0.4;
  transition: opacity 0.3s;
  &:hover {
    opacity: 0.8;
  }
`;

const StyledOption = styled(FormControlLabel)<{ selected: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  border-radius: 50px;
  border: 1px solid ${({ theme }) => theme.palette.primary.main};
  background: ${({ theme, selected }) => (selected ? theme.palette.primary.main : 'transparent')};
  color: ${({ theme, selected }) => (selected ? theme.palette.common.black : theme.palette.text.primary)};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  padding-left: 12px;

  &.MuiFormControlLabel-root {
    margin-left: 0px;
    margin-right: 12px;
    margin-bottom: 12px;
  }
`;

const StyledRadio = styled(Radio)<{ selected: number }>`
  &.MuiRadio-root {
    color: ${({ theme, selected }) => (selected ? theme.palette.common.black : theme.palette.common.white)};
  }
`;
